import { BaseEntityType } from 'types/entities';

// ---------------------------------------------------------------------------
// Credential types
// ---------------------------------------------------------------------------

export type CredentialType =
  | 'login'
  | 'payment_card'
  | 'identity'
  | 'secure_note'
  | 'api_key'
  | 'oauth_app'
  | 'token'
  | 'passkey'
  | 'custom';

// Fields blobs — what gets Fernet-encrypted server-side
export interface LoginFields {
  password: string;
  totp?: string;
}

export interface PaymentCardFields {
  cardNumber: string;
  cvv: string;
  expiryMonth: string;
  expiryYear: string;
  cardholderName: string;
  billingAddress?: string;
}

export interface IdentityFields {
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  address?: string;
  company?: string;
  ssn?: string;
  passport?: string;
  driverLicense?: string;
}

export interface SecureNoteFields {
  content: string;
}

export interface ApiKeyFields {
  key: string;
}

export interface TokenFields {
  token: string;
}

export interface OAuthAppFields {
  clientId: string;
  clientSecret: string;
  redirectUri?: string;
}

export interface PasskeyFields {
  credentialId: string;    // base64url-encoded credential ID
  rpId: string;            // relying party ID (e.g. "stripe.com")
  userHandle: string;      // base64url user handle
  publicKey: string;       // COSE-encoded public key (base64)
  counter: number;
  transports?: string[];   // ["internal", "hybrid", ...]
  deviceName: string;      // "MacBook Pro Touch ID"
}

export interface CustomFields {
  [key: string]: string;
}

export type CredentialFields =
  | LoginFields
  | PaymentCardFields
  | IdentityFields
  | SecureNoteFields
  | ApiKeyFields
  | TokenFields
  | OAuthAppFields
  | PasskeyFields
  | CustomFields;

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

export interface CredentialSkeleton {
  '@type': 'Credential';
  credentialType: CredentialType;
  /** Fernet-encrypted JSON blob — only present when fetched via GET /{id} */
  fields?: CredentialFields;
  /** Service slug (github, stripe, google, …) — stored unencrypted */
  service: string;
  /** URL for logins — stored unencrypted */
  url: string;
  /** Username/email — stored unencrypted for display and search */
  username: string;
  description: string;
  tags: string[];
  updatedAt: string;
  /** Entity ID of a linked APIConnection or Integration entity */
  linkedEntityId?: string;
}

// ---------------------------------------------------------------------------
// Entity
// ---------------------------------------------------------------------------

export interface CredentialEntity extends BaseEntityType {
  entityType: 'Credential';
  /** Human label: "Gmail", "Chase Visa", "GitHub OAuth App" */
  name: string;
  skeleton: CredentialSkeleton;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export const CREDENTIAL_TYPE_LABELS: Record<CredentialType, string> = {
  login: 'Login',
  payment_card: 'Payment Card',
  identity: 'Identity',
  secure_note: 'Secure Note',
  api_key: 'API Key',
  oauth_app: 'OAuth App',
  token: 'Token',
  passkey: 'Passkey',
  custom: 'Custom',
};

export const CREDENTIAL_TYPE_ICONS: Record<CredentialType, string> = {
  login: 'Password',
  payment_card: 'CreditCard',
  identity: 'IdentificationCard',
  secure_note: 'Note',
  api_key: 'Key',
  oauth_app: 'GitBranch',
  token: 'Shield',
  passkey: 'Fingerprint',
  custom: 'Archive',
};

/** View modes for the PasswordVaultApp */
export type VaultView = 'list' | 'detail' | 'add' | 'edit';

/** Category used in the sidebar nav — api_key, oauth_app, and token all map to 'api_key' */
export type VaultCategory = 'all' | 'login' | 'payment_card' | 'identity' | 'secure_note' | 'api_key';

export function credentialTypeToCategory(credentialType: CredentialType): VaultCategory {
  if (credentialType === 'api_key' || credentialType === 'oauth_app' || credentialType === 'token' || credentialType === 'passkey') {
    return 'api_key';
  }
  if (credentialType === 'custom') return 'all';
  return credentialType as VaultCategory;
}

export const CATEGORY_LABELS: Record<VaultCategory, string> = {
  all: 'All Items',
  login: 'Logins',
  payment_card: 'Payment Cards',
  identity: 'Identities',
  secure_note: 'Secure Notes',
  api_key: 'API Keys',
};
